/**
 * The end-of-day insights: the things people screenshot. Derived from final scores only (there is
 * no point-by-point), so every one is honest about what it can know. Empty until there are games.
 */

import { gameResult } from "./scoring";
import { displayName, pairKey, playerGames } from "./selectors";
import type { PlayerId, TournamentState, Where } from "./types";

export interface Insight {
  id: string;
  title: string;
  /** One line, names included, for the projector and the export. */
  detail: string;
  players: PlayerId[];
}

interface PlayedGame {
  roundNo: number;
  court: number;
  a: [PlayerId, PlayerId];
  b: [PlayerId, PlayerId];
  sa: number;
  sb: number;
  eventId: string | null;
  capped: boolean;
}

/** `where` decides which name a line uses: the wall shows nicknames, an export the real names. */
export function insights(state: TournamentState, where: Where = "wall"): Insight[] {
  const name = (id: PlayerId): string => (state.players[id] ? displayName(state.players[id], where) : id);
  const team = (p: readonly PlayerId[]): string => p.map(name).join(" & ");
  const games: PlayedGame[] = [];
  for (const r of state.rounds) {
    for (const g of r.games) {
      if (g.voided || !g.score) continue;
      games.push({ roundNo: r.no, court: g.court, a: g.a, b: g.b, sa: g.score.a, sb: g.score.b, eventId: g.eventId, capped: g.score.capped });
    }
  }
  if (!games.length) return [];
  const out: Insight[] = [];
  const line = (g: PlayedGame): string => {
    const [w, l, ws, ls] = g.sa > g.sb ? [g.a, g.b, g.sa, g.sb] : [g.b, g.a, g.sb, g.sa];
    return `${team(w)} ${ws}-${ls} ${team(l)}, round ${g.roundNo}`;
  };

  // biggest win and closest finish, until the awards are handed out: two of the awards are these
  if (!state.reveal.awards) {
    const biggest = [...games].sort((x, y) => Math.abs(y.sa - y.sb) - Math.abs(x.sa - x.sb) || y.sa + y.sb - (x.sa + x.sb))[0]!;
    out.push({ id: "biggest", title: "Biggest win", detail: line(biggest), players: [...biggest.a, ...biggest.b] });

    // closest finish: smallest margin, then the most points
    const closest = [...games].sort((x, y) => Math.abs(x.sa - x.sb) - Math.abs(y.sa - y.sb) || y.sa + y.sb - (x.sa + x.sb))[0]!;
    if (closest !== biggest) out.push({ id: "closest", title: "Closest finish", detail: line(closest), players: [...closest.a, ...closest.b] });
  }

  // longest game: the most points in one game
  const longest = [...games].sort((x, y) => y.sa + y.sb - (x.sa + x.sb))[0]!;
  if (longest.sa + longest.sb > 20) out.push({ id: "longest", title: "Longest game", detail: `${longest.sa + longest.sb} points: ${line(longest)}`, players: [...longest.a, ...longest.b] });

  // clean sheet
  const clean = games.find((g) => g.sa === 0 || g.sb === 0);
  if (clean) out.push({ id: "clean", title: "Clean sheet", detail: line(clean), players: [...clean.a, ...clean.b] });

  // per player totals
  const totals = new Map<PlayerId, { games: number; pf: number; pa: number; wins: number }>();
  for (const r of state.rounds) {
    for (const g of r.games) {
      const res = gameResult(g, state.config);
      if (!res) continue;
      for (const side of [res.a, res.b]) {
        for (const id of side.players) {
          const t = totals.get(id) ?? { games: 0, pf: 0, pa: 0, wins: 0 };
          t.games += 1;
          t.pf += side.raw;
          t.pa += side.rawAgainst;
          t.wins += side.won ? 1 : 0;
          totals.set(id, t);
        }
      }
    }
  }
  const byName = (x: PlayerId, y: PlayerId): number => name(x).localeCompare(name(y), "en");
  const ids = [...totals.keys()].sort(byName);

  const mostGames = Math.max(...ids.map((id) => totals.get(id)!.games));
  const ironmen = ids.filter((id) => totals.get(id)!.games === mostGames);
  out.push({ id: "iron", title: mostGames === 1 ? "On court" : "Most games", detail: `${ironmen.map(name).join(", ")}: ${mostGames} game${mostGames === 1 ? "" : "s"}`, players: ironmen });

  const mostPoints = Math.max(...ids.map((id) => totals.get(id)!.pf));
  const scorers = ids.filter((id) => totals.get(id)!.pf === mostPoints);
  out.push({ id: "points", title: "Points machine", detail: `${scorers.map(name).join(", ")}: ${mostPoints} points scored`, players: scorers });

  const walls = ids.filter((id) => totals.get(id)!.games >= 3);
  if (walls.length) {
    const against = (id: PlayerId): number => totals.get(id)!.pa / totals.get(id)!.games;
    const best = Math.min(...walls.map(against));
    const wall = walls.filter((id) => Math.abs(against(id) - best) < 1e-9);
    out.push({ id: "wall", title: "The wall", detail: `${wall.map(name).join(", ")}: only ${best.toFixed(1)} points conceded a game`, players: wall });
  }

  // best partnership: the pair with the biggest margin in their game together
  const pairs = new Map<string, { players: [PlayerId, PlayerId]; margin: number; text: string }>();
  for (const g of games) {
    for (const [p, mine, theirs] of [[g.a, g.sa, g.sb], [g.b, g.sb, g.sa]] as const) {
      const key = pairKey(p[0], p[1]);
      const margin = mine - theirs;
      const have = pairs.get(key);
      if (!have || margin > have.margin) pairs.set(key, { players: [p[0], p[1]], margin, text: `${team(p)} won ${mine}-${theirs} in round ${g.roundNo}` });
    }
  }
  const bestPair = [...pairs.values()].sort((x, y) => y.margin - x.margin)[0];
  if (bestPair && bestPair.margin > 0) out.push({ id: "pair", title: "Best partnership", detail: bestPair.text, players: bestPair.players });

  // events
  const eventWins = new Map<PlayerId, number>();
  for (const g of games) {
    if (!g.eventId) continue;
    const winners = g.sa > g.sb ? g.a : g.b;
    for (const id of winners) eventWins.set(id, (eventWins.get(id) ?? 0) + 1);
  }
  if (eventWins.size) {
    const top = Math.max(...eventWins.values());
    const heroes = [...eventWins.entries()].filter(([, n]) => n === top).map(([id]) => id).sort(byName);
    out.push({ id: "events", title: "Mini-game heroes", detail: `${heroes.map(name).join(", ")}: ${top} mini-game win${top === 1 ? "" : "s"}`, players: heroes });
  }

  const onClock = games.filter((g) => g.capped).length;
  if (onClock) out.push({ id: "clock", title: "Beat the clock", detail: `${onClock} of ${games.length} games ${onClock === 1 ? "was" : "were"} ended by the whistle`, players: [] });

  // the anti-awards (decision 27): for the reel, not the podium
  const stats = Object.keys(state.players).map((id) => {
    const mine = playerGames(state, id).filter((g) => g.won !== null && g.score);
    let run = 0;
    let worst = 0;
    for (const g of mine) {
      run = g.won ? 0 : run + 1;
      worst = Math.max(worst, run);
    }
    const scored = mine.reduce((n, g) => n + (g.score ? g.score[0] : 0), 0);
    const conceded = mine.reduce((n, g) => n + (g.score ? g.score[1] : 0), 0);
    return { id, games: mine.length, avg: mine.length ? scored / mine.length : 0, worst, conceded };
  });
  const regulars = stats.filter((s) => s.games >= Math.max(1, state.config.minGamesForPodium));
  if (regulars.length >= 4) {
    const low = Math.min(...regulars.map((s) => s.avg));
    const spoon = regulars.filter((s) => Math.abs(s.avg - low) < 1e-9).map((s) => s.id).sort(byName);
    out.push({ id: "spoon", title: "The wooden spoon", detail: `${spoon.map(name).join(", ")}: ${low.toFixed(1)} points a game`, players: spoon });
  }
  const longestRun = Math.max(0, ...stats.map((s) => s.worst));
  if (longestRun >= 3) {
    const who = stats.filter((s) => s.worst === longestRun).map((s) => s.id).sort(byName);
    out.push({ id: "losing", title: "Longest losing run", detail: `${who.map(name).join(", ")}: ${longestRun} in a row`, players: who });
  }
  const mostConceded = Math.max(0, ...stats.map((s) => s.conceded));
  if (mostConceded > 0 && stats.filter((s) => s.games > 0).length >= 4) {
    const who = stats.filter((s) => s.conceded === mostConceded).map((s) => s.id).sort(byName);
    out.push({ id: "sieve", title: "The open door", detail: `${who.map(name).join(", ")}: ${mostConceded} points let in`, players: who });
  }

  return out;
}
